import useFormatter from "@/hooks/useFormatter";
import { calcSilverWeight } from "@/lib/transactionCalculations";
import StatValue from "@/components/common/StatValue";
import { StatGridSkeleton } from "@/components/loading";
import useConfirm from "@/hooks/useConfirm";
import { statsKeys, transactionsKeys } from "@/query-keys";
import { services } from "@/services";
import type { TransactionItemForm, TransactionListItem } from "@/schemas/transactionSchema";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Scale, Banknote, Gem, Clock, Weight, Wrench, Wallet, Printer, List, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { useCallback, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import type { ColumnDef } from "@tanstack/react-table";
import useClientStatsQuery from "../_hooks/useClientStatsQuery";
import useClientTransactionsQuery from "../_hooks/useClientTransactionsQuery";
import useLastTransactionQuery from "../_hooks/useLastTransactionQuery";
import useColumns from "../../transactions/_hooks/useColumns";
import useTransactionMutation from "../../transactions/_hooks/useTransactionMutation";
import KaratMismatchIndicator from "../../transactions/_components/KaratMismatchIndicator";
import PrintableTransaction, { receiptFileName } from "@/components/PrintableTransaction";

export const Route = createFileRoute("/_private/clients/$clientId/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: async ({ params, deps, context: { queryClient } }) => {
    const { clientId } = params;
    const requestParams = mapTableStateToQueryParams(safeDecompress(deps.q));
    requestParams.set("clientId", clientId);

    const [stats, transactions] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: statsKeys.clients(clientId),
        queryFn: () => services.getClientStats(clientId),
      }),
      queryClient.ensureQueryData({
        queryKey: [...transactionsKeys.lists(deps.q), "client", clientId],
        queryFn: () => services.getTransactions(requestParams),
      }),
    ]);

    return { stats, transactions };
  },
  component: ClientDetailPage,
});

function ClientDetailPage() {
  const { t } = useTranslation();
  const formatter = useFormatter();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { clientId } = Route.useParams();
  const { stats: initialStats, transactions: initialTransactions } = Route.useLoaderData();
  const { data: stats, isFetching } = useClientStatsQuery(clientId, initialStats);
  const { data: transactions } = useClientTransactionsQuery(
    clientId,
    search.q,
    initialTransactions,
  );

  const { lastTx, previousGoldBalance, previousCashBalance } = useLastTransactionQuery(
    clientId,
    stats,
  );

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    ignoreGlobalStyles: true,
    documentTitle: lastTx ? receiptFileName(lastTx) : "receipt",
  });

  const columns = useColumns().map(
    (c): ColumnDef<TransactionListItem> =>
      c.id === "clientName"
        ? {
            ...c,
            id: "type",
            accessorKey: "type",
            meta: {
              type: "radio",
              options: [
                { value: "inbound", label: "inbound" },
                { value: "outbound", label: "outbound" },
              ],
            },
            header: t("transactions.columns.type"),
            cell: ({ row }: { row: { original: TransactionListItem } }) => (
              <div className="flex items-center gap-1.5">
                <Badge variant={row.original.type === "inbound" ? "success" : "info"}>
                  {t(`transactions.type.${row.original.type}`)}
                </Badge>
                <KaratMismatchIndicator items={row.original.items as TransactionItemForm[]} />
              </div>
            ),
          }
        : c,
  );
  const { deleteTransaction, isDeleting } = useTransactionMutation();

  const deleteConfirm = useConfirm({
    title: t("transactions.confirm.delete.title"),
    description: t("transactions.confirm.delete.description"),
    isPending: isDeleting,
  });

  const handleView = useCallback(
    (row: TransactionListItem) =>
      navigate({ to: "/transactions/$transactionId", params: { transactionId: row.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (row: TransactionListItem) => {
      deleteConfirm.confirm(() => {
        deleteTransaction(row.id);
      });
    },
    [deleteConfirm, deleteTransaction],
  );

  return (
    <div className="flex-1 flex flex-col gap-8">
      <div className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <Scale className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("clients.detail.stats.title")}
          </h2>
          {isFetching && (
            <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
          )}
          <Button
            variant="outline"
            size="sm"
            className="ms-auto"
            disabled={!lastTx}
            onClick={() => handlePrint()}
          >
            <Printer className="h-3.5 w-3.5" />
            {t("clients.detail.printLastTransaction")}
          </Button>
        </div>
        {!stats ? (
          <StatGridSkeleton cols={3} />
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatValue
            icon={<Gem className="h-4 w-4" />}
            label={t("clients.detail.stats.goldBalance")}
            sub={`${t("stats.summary.subs.goldBalance")}  •  ${t("stats.summary.fields.pureWeightAdded")} (${formatter.weight(stats.totalWeightMoved, 3)})`}
            value={stats ? formatter.weight(Math.abs(stats.goldBalance), 3) : null}
            trend={stats ? (stats.goldBalance >= 0 ? "positive" : "negative") : undefined}
          />
          <StatValue
            icon={<Banknote className="h-4 w-4" />}
            label={t("clients.detail.stats.cashBalance")}
            sub={t("stats.summary.subs.cashBalance")}
            value={stats ? formatter.currency(Math.abs(stats.cashBalance)) : null}
            trend={stats ? (stats.cashBalance >= 0 ? "positive" : "negative") : undefined}
          />
          <StatValue
            icon={<Weight className="h-4 w-4" />}
            label={t("transactions.form.silverWeight")}
            value={stats ? formatter.weight(calcSilverWeight(stats.inboundPureWeight), 3) : null}
            sub={
              stats
                ? `${t("stats.summary.fields.inboundPureWeight")}: ${formatter.weight(stats.inboundPureWeight, 3)}`
                : ""
            }
          />
          <StatValue
            icon={<Wrench className="h-4 w-4" />}
            label={t("clients.detail.stats.totalLabor")}
            sub={t("clients.detail.stats.totalLaborHint")}
            value={stats ? formatter.currency(stats.totalLabor) : null}
          />
          <StatValue
            icon={<Wallet className="h-4 w-4" />}
            label={t("clients.detail.stats.totalDeposit")}
            sub={t("clients.detail.stats.totalDepositHint")}
            value={stats ? formatter.currency(stats.totalDeposit) : null}
          />
          <StatValue
            icon={<Clock className="h-4 w-4" />}
            label={t("clients.detail.stats.lastTransactionAt")}
            sub={t("clients.detail.stats.lastTransactionAtHint")}
            value={
              stats?.lastTransactionAt ? formatter.relativeTime(stats.lastTransactionAt) : null
            }
          />
        </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <List className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("clients.detail.transactions")}
          </h2>
        </div>

        <DataTable<TransactionListItem>
          queryKey={[...transactionsKeys.lists(search.q), "client", clientId]}
          data={transactions?.items}
          nextCursor={transactions?.nextCursor}
          hasMore={transactions?.hasMore}
          columns={columns}
          searchPlaceholder={t("transactions.list.searchPlaceholder")}
          navigateFrom="/clients/$clientId"
          searchFrom="/_private/clients/$clientId/"
          onView={handleView}
          onDelete={handleDelete}
          searchable={true}
        />
      </div>

      {lastTx && (
        <PrintableTransaction
          ref={printRef}
          transaction={lastTx}
          previousGoldBalance={previousGoldBalance}
          previousCashBalance={previousCashBalance}
        />
      )}

      {deleteConfirm.confirmDialog}
    </div>
  );
}
